'use strict';
import Timer from '../../lib/timer.js';
const { generateTargeting, generateNonlinearTargeting } = require('./videoAds');
const { getDependencyClass } = require('../../lib/dependencies.js');
const device = require('../../config/device');
const performanceMeasure = require('../../lib/performanceMeasure.js');
const permutive = require('./permutive.js');
let measure = performanceMeasure('webcomponents:video');
const callbackQueuePromise = require('../../lib/callbackQueuePromise');
const clientConfigPromise = callbackQueuePromise('phxConfigQueue');

// Module-level variable to ensure the script is loaded only once. This follows
// the standard Phoenix web component pattern; as such, if we switch to
// a shared base class to capture common patterns, this should also be
// migrated.
let LoadScriptPromise;

// Comscore JWPlayer integration module promise
let ComscoreAnalyticsPromise;

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * <phoenix-video> Web Component.
     *
     * Attributes:
     * Mandatory:
     * - library-id, a publisher-specific JWPlayer ID.
     * - video-id, the JWPlayer API identifier for the video.
     *
     * Optional:
     * - ad, a JSON object containing advertising info, or the string "disabled",
     *   indicating that ads should not be displayed. In the expected run of
     *   events, this will usually _not_ be set initially, and phoenix-video will
     *   generate the ad info internally, checking for GDPR and Premium.
     *
     * - analytics-config - a JSON object with analytics configuration.
     *
     * - ad-config, a JSON object that contains the data necessary for
     *   displaying instream and nonlinear ads, including for running
     *   an auction for each video played by the player.
     *
     * - autoplay, a boolean indicating that the video should play as soon as it is
     *   visible, without viewer interaction. (This behavior is dependent on
     *   receiving a prebid bid above a defined threshold, representing
     *   roughly our cost of serving the video.)
     *
     * - delay-load, a boolean attribute indicating that the video should not be loaded
     *   until after the window.load event.
     *
     * - direct-only, an optional boolean indicating that we wish to disable
     *   the prebid auction for this video and use a `direct=1` parameter in
     *   our targeting.
     *
     * - height, width - by default we leave these undefined, which results in
     *   reactive sizing when we instantiate the video.
     *
     * - third-party-ids - an object containing third-party identifiers used to
     *   set up any outside integrations; at the moment, this only supports the
     *   Now/With shoppable layer.
     *
     * We also use an attribute, block-player-loading, set during connection, to
     * reflect state. When it goes away, we can load the player.
     */
    class PhoenixVideo extends pageClass.PhoenixElement {
        static get observedAttributes() {
            return ['block-player-loading'];
        }

        get observedBroadcastedEvents() {
            return ['phoenix:header:in', 'phoenix:header:out'];
        }

        /** Flag to know when the video is ready to float*/
        set canFloat(val) {
            this._canFloat = val;
        }

        get canFloat() {
            return this._canFloat;
        }

        set isMuteButtonPressed(val) {
            this._unmuteButton.setAttribute('aria-pressed', !val);
            this._unmuteButton.setAttribute('aria-hidden', !val);
            this._muteButton.setAttribute('aria-pressed', val);
            this._muteButton.setAttribute('aria-hidden', val);
        }

        set isFloating(val) {
            if (!this.canFloat) {
                return;
            }
            // User manually closed float player, so don't show it again on this page view
            if (this.floatWasClosed) {
                return;
            }

            this._isFloating = val;
            this.phoenixPage.classList.toggle('is-fnp-floating', val);
            this.parentContainer.classList.toggle('is-fnp-floating', val);
            if (this.player) {
                this.player.setFloating(val);
            }
        }

        get isFloating() {
            return this._isFloating;
        }

        set isUserInactive(val) {
            if (
                val &&
                !this._isUserInactive &&
                !this.dismissTimer &&
                this.isFloating &&
                this.dismissAfterTimeout > 0 &&
                this.player
            ) {
                this._isUserInactive = val;
                this.dismissTimer = new Timer(() => {
                    this.player.setFloating(false);
                    this.player.pause();
                    this.emitFnPSClosedStatus();
                }, this.dismissAfterTimeout * 1000);
            }

            if (!val && this.dismissTimer) {
                this.dismissTimer.cancel();
            }

            this._isUserInactive = val;
        }

        get isUserInactive() {
            return this._isUserInactive;
        }

        /**
         * Examines the `block-player-loading` attribute to decide if we should block the
         * video player from loading.
         *
         * If the attribute is present, we block, otherwise we don't.
         *
         * @readonly
         */
        get isPlayerLoadingBlocked() {
            return this.hasAttribute('block-player-loading');
        }

        get recommendationsPlaylist() {
            return this.getAttribute('recommendations-playlist') ?? false;
        }

        get playlist() {
            const isPlayList = this.hasAttribute('is-playlist');
            const usePhxVideoProxy = this.hasAttribute('use-phx-video-proxy');
            const baseUrl =
                usePhxVideoProxy && !isPlayList
                    ? '/.api/videoProxy/jw-player/v2'
                    : 'https://cdn.jwplayer.com/v2';
            const endpoint = isPlayList ? 'playlists' : 'media';
            return `${baseUrl}/${endpoint}/${this.videoId}${
                this.recommendationsPlaylist && !isPlayList
                    ? `?recommendations_playlist_id=${this.recommendationsPlaylist}`
                    : ''
            }`;
        }

        attributeChangedCallback(attrName) {
            // Only execute when this attribute is being removed
            if (attrName === 'block-player-loading' && !this.hasAttribute(attrName)) {
                try {
                    this._instantiatePlayer();
                } catch (err) {
                    this._reportErrorMessage('Error instantiating player: ' + err.toString());
                }
            }
        }

        connectedCallback() {
            super.connectedCallback();

            if (!window.GdprStatus) {
                throw new Error('window.GdprStatus is required');
            }

            // We always start with player blocked on the load of the JWPlayer library.
            this.setAttribute('block-player-loading', true);
            this.phoenixPage = this.closest('phoenix-page');
            this.parentContainer = this.closest('figure');
            this.libraryId = this.getAttr('library-id', { required: true });
            this.videoId = this.getAttr('video-id', { required: true });
            this.playerLoaded = false;

            clientConfigPromise.then(({ features }) => {
                this.features = features;

                if (this.features.isEnabled('jw-float-placement')) {
                    // Load the player immediately so that we can boost JW visibility.
                    // If the user has loaded in the middle of the page or they have
                    // scrolled down quickly FnP will be automatically enabled.
                    this.loadPlayer();
                } else {
                    // lazy-loading, don't do anything before the first intersection.
                    this.observeFirstIntersection();
                    this.observeAfterFirstFullView();
                }
            });
        }

        broadcastedEventCallback(name) {
            switch (name) {
                case 'phoenix:header:in':
                    this.isFloating = false;
                    this.canFloat = false;
                    break;
                case 'phoenix:header:out':
                    this.isFloating = true;
                    this.canFloat = true;
                    break;
            }
        }

        loadPlayer() {
            if (this.playerLoaded) return;

            this.playerLoaded = true;
            if (this.hasAttribute('delay-load')) {
                pageClass.util.whenPageLoaded().then(() => {
                    this._init();
                });
            } else {
                this._init();
            }
        }

        onFirstIntersection() {
            this.loadPlayer();
        }

        onAfterFirstFullView() {
            this.canFloat = true;
        }

        //Emit close Status when the float and park being closed.
        emitFnPSClosedStatus() {
            this.emitEvent('fnp:closed', {
                bubbles: true,
                detail: {},
            });
        }

        /**
         * Overridable method for any additional setup required by extensions.
         * Must return a promise.
         */
        additionalInitPromise() {
            return Promise.resolve(true);
        }

        /**
         * _init initializes the <phoenix-video> element.
         *
         * It is triggered as late as possible, once lazy-loading and delay-loading has been handled.
         */
        _init() {
            this.auctionCount = 0;
            this.gdprApplicable = false;
            this.adId = null;
            this.playerStates = { paused: false, playing: false, completed: false, viewable: 1 };
            this.showFooter = this.hasAttribute('show-footer');
            this.autoplayThreshold = this.getAttribute('autoplay-threshold') || 0;
            this.autoplayThresholdMet = !Number(this.autoplayThreshold) || this.autoplayThreshold <= 0;
            this.dismissAfterTimeout = parseInt(this.getAttr('dismiss-after-timeout') || 0);

            try {
                this.permutiveSegments = JSON.parse(localStorage.getItem('_pdfps') || '[]').slice(0, 250);
            } catch (error) {
                this.permutiveSegments = [];
            }

            let analyticsConfig = this.getAttr('analytics-config');
            if (analyticsConfig) {
                try {
                    this.analyticsConfig = JSON.parse(analyticsConfig);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('Unable to parse config attribute', e);
                }
            }

            // Retrieve the auction configuration for header bidding support
            // this video, if present. Assign the resulting object to the
            // `adConfig` property.
            let adConfig = this.getAttr('ad-config');
            if (adConfig) {
                try {
                    this.adConfig = JSON.parse(adConfig);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('Unable to parse ad-config attribute', e);
                }
            }

            // Outside identifiers which affect our video setup; at the moment
            // the only supported provider is Now/With.
            this.thirdPartyIds = {};
            if (this.getAttr('third-party-ids')) {
                try {
                    this.thirdPartyIds = JSON.parse(this.getAttr('third-party-ids'));
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('Unable to parse third-party-ids attribute', e);
                }
            }

            let additionalPromise = this.additionalInitPromise();

            // Finish out the setup behavior
            if (this.getAttribute('height') && this.getAttribute('width')) {
                // We generally don't want to do this, in order to get a reactive
                // video, but the option is there.
                this.dimensions = { h: this.getAttribute('height'), w: this.getAttribute('width') };
            }

            // Here, monetization state is used for our analytics and reporting.
            let monetizationState = new Set();
            this.directOnly = this.hasAttribute('direct-only');
            if (this.directOnly) {
                monetizationState.add('direct_only');
            }

            if (this.getAttr('ad-suppressed-reasons')) {
                try {
                    const reasons = JSON.parse(this.getAttr('ad-suppressed-reasons'));
                    reasons.forEach((x) => monetizationState.add(x));
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('Unable to parse ad-suppressed-reasons attribute', e);
                }
            }

            const libraryPromise = this._loadPlayerLibrary().catch((err) => {
                // if the library fails to load, report the error and propagate the error
                // to block the video from playing.
                this._reportErrorMessage('Cannot load player library: ' + JSON.stringify(err));
                throw err;
            });

            const demonetizedPromise = window.GdprStatus.isDemonetizedSafe();

            // Set up the blockers for instantiating the video
            Promise.all([
                libraryPromise,
                demonetizedPromise,
                pageClass.util.userHasPremium(),
                window.GdprStatus.isApplicable(),
                additionalPromise,
            ]).then((promises) => {
                // demonetize based on Maven UID service or
                // based on Tempest Premium
                this.demonetized = !!promises[1] || promises[2];

                if (promises[1]) {
                    monetizationState.add('gdpr');
                }
                if (promises[2]) {
                    monetizationState.add('premium');
                }

                this.analyticsModel = {
                    videoMonetizationState: monetizationState.size
                        ? [...monetizationState.values()].join('|')
                        : 'OK',
                };

                this.gdprApplicable = promises[3];

                // start initial auction for first video; if we do not have an
                // autoplay threshold we can remove the block now.
                if (this.autoplayThresholdMet) {
                    this.removeAttribute('block-player-loading');
                }
                this.runAuction().then(() => {
                    // The auction is complete so we can always unblock now.
                    this.removeAttribute('block-player-loading');
                });
            });
        }

        /**
         * _reportErrorMessage logs the error and pings Google Analytics.
         * @param {String} errMessage
         */
        _reportErrorMessage(errMessage) {
            if (window.googleAnalytics) {
                window.googleAnalytics?.sendEvent?.('send', 'event', 'phoenixVideoError', errMessage);
            }
            // eslint-disable-next-line no-console
            console.error('phoenix-video error: ' + errMessage);
        }

        /**
         * Helper promise function to affirm that when GDPR applies, consent
         * has been given for the analytics (measurement) purpose. Resolves if
         * consent is given (or if consent is unnecessary); rejects when consent
         * has not been given.
         */
        _consentForAnalytics() {
            // GPP check
            const gppPromise = window.phxConsentManagement.gppAccepted();

            // GDPR check
            const gdpr = window.GdprStatus;
            const gdprPromise = new Promise((resolve, reject) => {
                gdpr.isApplicable().then((isApplicable) => {
                    if (isApplicable) {
                        gdpr.waitForConsentValue().then(() => {
                            gdpr.isConsentGivenForPurposes(['measurement']).then(resolve, reject);
                        });
                    } else {
                        resolve(true);
                    }
                }, reject);
            });

            return Promise.all([gppPromise, gdprPromise]).then((consents) => {
                return consents[0] && consents[1];
            });
        }

        /**
         * Helper function to load a script resource. Returns a Promise that
         * resolves once the script has been loaded. Rejects when the script
         * fails to load.
         * @param {string} id A unique id to apply to the created script tag.
         * @param {string} src The full URL of the script resource to load.
         */
        _loadScript(id, src) {
            return pageClass.util.addScriptResourceOnce(src, { id });
        }

        /**
         * Loads the JWPlayer library for all <phoenix-video> elements.
         *
         * Injects the script in <head> for this element's library ID; load the
         * JWPlayer script only once per browser session.
         *
         * @returns {Promise} A Promise for when the script has loaded.
         */
        _loadPlayerLibrary() {
            /* Note that we can have many phoenix-videos on the page, but we must
             * have only one library. JWPlayer inserts non-namespaced values into
             * the window object that it uses in its own code, and use of
             * multiple libraries on a single page is unsupported.
             */
            if (!LoadScriptPromise) {
                if (window.jwplayer) {
                    // in case something else than <phoenix-video> has loaded JWPlayer.
                    return Promise.resolve();
                }
                LoadScriptPromise = this._loadScript(
                    'phoenixvideo_jwp',
                    'https://content.jwplatform.com/libraries/' + this.libraryId + '.js'
                );
            }
            return LoadScriptPromise;
        }

        _loadComscoreLibrary() {
            if (window.ns_) {
                ComscoreAnalyticsPromise = Promise.resolve();
            } else {
                ComscoreAnalyticsPromise =
                    ComscoreAnalyticsPromise ||
                    this._loadScript(
                        'phoenixvideo-comscoreanalytics-jwp',
                        'https://sb.scorecardresearch.com/c2/plugins/streamingtag_plugin_jwplayer.js'
                    );
            }
            return ComscoreAnalyticsPromise;
        }

        unmuteSound() {
            this.player.setMute(false);
            this.isMuteButtonPressed = false;
        }

        muteSound() {
            this.player.setMute(true);
            this.isMuteButtonPressed = true;
        }

        _initControlsContainer() {
            const initButtons = (parentEl) => {
                if (!parentEl) {
                    return;
                }

                this._muteButton = parentEl.querySelector('.m-video-player--mute');
                this._unmuteButton = parentEl.querySelector('.m-video-player--unmute');

                let closeButton = parentEl.querySelector('.m-video-player--close');

                const toggleButtons = () => {
                    const isMute = this.player.getMute();
                    this._unmuteButton?.classList.toggle('is-active-button', isMute);
                    this._muteButton?.classList.toggle('is-active-button', !isMute);
                };
                this.player.on('mute', toggleButtons);
                toggleButtons();

                this._unmuteButton?.addEventListener('click', this.unmuteSound.bind(this));

                this._muteButton?.addEventListener('click', this.muteSound.bind(this));

                closeButton?.addEventListener('click', () => {
                    // if the user closes the floating video, we want to pause it.
                    this.playerStates.paused = true;
                    this.player.pause();
                    this.isFloating = false;
                    // If user closed the float, assume they don't want to see it again
                    // even if they restart the video
                    this.floatWasClosed = true;
                });
            };

            const staticFooter = document.querySelector(`.${this.id}-footer`);
            const staticContainer = document.querySelector(`.m-video-player--frame-container`);
            const floatContainer = this.querySelector('.m-video-player--controls-float');

            [staticContainer, floatContainer].forEach(initButtons);

            this.player.on('mute', () => {
                if (window.googleAnalytics) {
                    const customMap = {
                        video_title: this.analyticsModel.videoTitle || this.playerConfig.playlist.title,
                        page_url: window.location.href,
                    };

                    window.googleAnalytics.sendEvent(
                        'Video Mute/Unmute',
                        'click',
                        this.player.getMute(),
                        null,
                        customMap
                    );
                }
            });
            if (staticFooter) {
                this.staticFooterMeta = staticFooter.querySelector('.m-video-player--footer-meta');
            }
            this.floatFooterMeta = this.querySelector('.m-video-player--footer-meta');
        }

        /**
         * The actual player setup; overridable in subclasses.
         */
        setupPlayer() {
            this.player.setup(this.playerConfig);
            this.player.setPlaylistItemCallback(this.onPlaylistItem.bind(this));
            if (this.hasAttribute('floating')) {
                // We can't simply use { once: true } until we drop IE11
                // support.
                this.floatScrollHandler = this.scrollListener.bind(this);
                window.addEventListener('scroll', this.floatScrollHandler);
            }
        }

        /**
         * Creates the JWPlayer element and sets it up with the current player config.
         *
         * Only works once the library has loaded and a video ID is set.
         *
         */
        _instantiatePlayer() {
            let playerDiv = document.createElement('div');
            let floatFooter = this.querySelector('.m-video-player--footer-float');
            if (floatFooter) {
                // So we can use adjacent sibling combinator to show footer when player is floating
                this.insertBefore(playerDiv, floatFooter);
            } else {
                this.appendChild(playerDiv);
            }

            this.player = window.jwplayer(playerDiv);

            this.setupPlayer();

            this.waitForElement('.jwplayer').then((player) => {
                this.playerElement = player;
                let jwplayerStates = this._jwplayerStates.bind(this);
                const classNameObserver = new MutationObserver(jwplayerStates);
                classNameObserver.observe(player, {
                    attributes: true,
                    attributeFilter: ['class'],
                });

                // Begin the FnP as soon as the JWPlayer is loaded and the user has scrolled
                // beyond the video placeholder. In our current setup, should users scroll
                // past the video prior to the script being ready, they are required to scroll
                // back up, making the video visible again to trigger the FnP behavior.
                if (this.canFloat && this.features.isEnabled('jw-float-placement')) {
                    this.isFloating = true;
                }
            });

            // Set up mute/unmute buttons and video playlist meta
            if (this.id) {
                this._initControlsContainer();
            }

            // Skip to time offset if passed in hash (e.g. #t=20)
            let matches = (window.location.hash || '').match(/t=(\d+)/);
            if (matches) {
                // Remove hash from URL so video will start at beginning if it's shared
                if (history.pushState) {
                    history.pushState('', document.title, window.location.pathname + window.location.search);
                }
                this.offset = Number(matches[1]);
                this.player.on('firstFrame', () => {
                    if (this.offset) {
                        this.player.seek(this.offset);
                        this.offset = false;
                    }
                });
            }

            let adPositionInSecondsTracked = {};

            this.player.on('adClick', (ad) => {
                this.sendPermutiveEvent('VideoAdClicked', ad);
            });

            this.player.on('adImpression', (ad) => {
                this.sendPermutiveEvent('VideoAdView', ad);
            });

            this.player.on('adError', (error) => {
                /* This is not a fatal error in the sense of reporting an error via
                 * Google Analytics or not being able to display the video. It does
                 * mean that we're not getting use of the inventory--in the future,
                 * we should probably figure out a consolidated mechanism for
                 * reporting these sorts of errors in phoenix-video and in
                 * phoenix-ad-outstream.
                 */
                let message = 'phoenix-video JWPlayer ad error: ' + JSON.stringify(error);
                // eslint-disable-next-line no-console
                console.warn(message);
                this.sendLiftIgniterEvent({
                    e: 'video_ad_error',
                    errorString: error.message,
                    adTag: error.tag,
                });
            });

            const videoParams = {
                video_id: this.videoId,
                video_title: this.analyticsModel.videoTitle || this.playerConfig.playlist.title,
                video_author: this.analyticsModel.videoAuthorName,
            };

            let videoId = this.videoId,
                videoUrl = '',
                videoDuration = null,
                prevPosition = 0;
            // Enables GA tracking for player events.
            window.googleAnalyticsPromise.then(() => {
                let positionTracked = {},
                    positionInSecondsTracked = {};

                this.player.on('playlistItem', (ev) => {
                    videoUrl = ev.item.file;
                    videoDuration = ev.item.duration;

                    // Reset when a new video is loaded.
                    if (ev.item.mediaid !== videoId) {
                        videoId = ev.item.mediaid;
                        positionTracked = {};
                    } else if (this.playerStates.completed) {
                        this.sendLiftIgniterEvent({
                            ...ev,
                            e: 'video_ad_and_video_replay',
                            id: this.adId,
                            videoDuration,
                            videoUrl,
                            videoId,
                            videoIndex: this.auctionCount,
                        });
                    }
                });

                this.player.on('firstFrame', (ev) => {
                    window.googleAnalytics.sendEventCustomDimensions('Video', 'Play', videoId, undefined, {
                        ...this.analyticsModel,
                        ...videoParams,
                    });
                    positionInSecondsTracked = {};
                    adPositionInSecondsTracked = {};
                });

                this.player.on('time', (ev) => {
                    const played = (ev.position / ev.duration) * 100;

                    [25, 50, 75, 95, 100].forEach((percent) => {
                        if (positionTracked[percent]) {
                            // A beacon is already fired for this percentage.
                            return;
                        }

                        if (played >= percent) {
                            window.googleAnalytics.sendEventCustomDimensions(
                                'Video',
                                'PlayPercent',
                                videoId,
                                percent,
                                {
                                    ...this.analyticsModel,
                                    ...videoParams,
                                }
                            );
                            if (percent === 100) {
                                window.googleAnalytics.sendEventCustomDimensions(
                                    'Video',
                                    'CompleteJw',
                                    videoId,
                                    percent,
                                    {
                                        ...this.analyticsModel,
                                        ...videoParams,
                                    }
                                );
                            }
                            positionTracked[percent] = true;
                        }
                    });

                    const checkpoints = [15, 30];
                    checkpoints.forEach((position) => {
                        if (positionInSecondsTracked[position]) {
                            // A beacon is already fired for this percentage.
                            return;
                        }

                        const nextIndex = checkpoints.indexOf(position) + 1;
                        if (nextIndex <= checkpoints.length) {
                            if (ev.position > checkpoints[nextIndex]) {
                                // The user skipped to the next checkpoint, do not fire for this position
                                return;
                            }
                        }

                        if (ev.position >= position) {
                            if (ev.position - prevPosition > 1) {
                                this.sendLiftIgniterEvent({
                                    ...ev,
                                    e: 'video_play_skipped_past_' + position + 's',
                                    videoDuration,
                                    videoUrl,
                                    videoId,
                                    videoIndex: this.auctionCount,
                                });
                            } else {
                                this.sendLiftIgniterEvent({
                                    ...ev,
                                    e: 'video_play_stuck_' + position + 's',
                                    videoDuration,
                                    videoUrl,
                                    videoId,
                                    videoIndex: this.auctionCount,
                                });
                            }

                            positionInSecondsTracked[position] = true;
                        }
                    });

                    prevPosition = ev.position;
                });
            });

            // Comscore Analytics
            if (
                this.analyticsConfig &&
                this.analyticsConfig.comscore &&
                this.analyticsConfig.comscore.config &&
                this.analyticsConfig.comscore.config.length > 0
            ) {
                // We're only considering the first configured Comscore entry;
                // there's no way to supply multiple publisher ids.
                const config = this.analyticsConfig.comscore.config[0];
                const comscoreLibrary = this._loadComscoreLibrary();
                Promise.all([this._consentForAnalytics(), comscoreLibrary]).then((results) => {
                    const consented = results[0];
                    if (
                        consented &&
                        window.ns_ &&
                        window.ns_.StreamingAnalytics &&
                        window.ns_.StreamingAnalytics.JWPlayer
                    ) {
                        let labelmapping;
                        if (config.videoTracking && config.videoTracking.staticReportingValues) {
                            labelmapping = config.videoTracking.staticReportingValues
                                .filter((kv) => kv.value)
                                .map((kv) => kv.key + '="' + kv.value.replace(/"/g, '') + '"')
                                .join(', ');
                        }
                        window.ns_.StreamingAnalytics.JWPlayer(this.player, {
                            publisherId: config.publisherId,
                            labelmapping,
                        });
                    }
                });
            }

            // LiftIgniter video tracking
            this.player.on('viewable', (ev) => {
                this.playerStates.viewable = ev.viewable;
            });

            this.player.on('levelsChanged', (ev) => {
                this.sendLiftIgniterEvent({
                    ...ev,
                    e: 'video_quality_level_changed',
                    videoDuration,
                    videoUrl,
                    videoId,
                    videoIndex: this.auctionCount,
                });
            });

            this.player.on('adPlay', (ev) => {
                this.adId = ev.id;
                this.sendLiftIgniterEvent({
                    ...ev,
                    e: this.playerStates.paused ? 'video_ad_resume' : 'video_ad_play',
                    id: this.adId,
                });
                this.playerStates.paused = false;
            });

            this.player.on('adPause', (ev) => {
                this.playerStates.paused = true;
                if (!this.playerStates.viewable) {
                    // when the user clicks on the X button in the floating UI, the ad pauses.
                    // JWPlayer 'float' event alone does't uniquely identify when the X button is pressed
                    // because scrolling back up also unfloats the UI
                    this.sendLiftIgniterEvent({ ...ev, e: 'video_ad_unfloat', id: this.adId });
                }
                this.sendLiftIgniterEvent({ ...ev, e: 'video_ad_pause', id: this.adId });
            });

            this.player.on('adComplete', (ev) => {
                this.sendLiftIgniterEvent({ ...ev, e: 'video_ad_complete', id: this.adId });
                this.adId = null;
            });

            this.player.on('adSkipped', (ev) => {
                this.sendLiftIgniterEvent({ ...ev, e: 'video_ad_skipped', id: this.adId });
                this.adId = null;
            });

            this.player.on('adTime', (ev) => {
                const checkpoints = [15, 30];
                checkpoints.forEach((position) => {
                    if (adPositionInSecondsTracked[position]) {
                        // A beacon is already fired for this percentage.
                        return;
                    }

                    const nextIndex = checkpoints.indexOf(position) + 1;
                    if (nextIndex <= checkpoints.length) {
                        if (ev.position > checkpoints[nextIndex]) {
                            // The user skipped to the next checkpoint, do not fire for this position
                            return;
                        }
                    }

                    if (ev.position >= position) {
                        this.sendLiftIgniterEvent({
                            ...ev,
                            e: 'video_ad_play_stuck_' + position + 's',
                            id: this.adId,
                        });
                        adPositionInSecondsTracked[position] = true;
                    }
                });
            });

            this.player.on('play', (ev) => {
                if (!this.playerStates.playing || this.playerStates.paused) {
                    this.adId = null;
                    this.sendLiftIgniterEvent({
                        ...ev,
                        e: this.playerStates.paused ? 'video_resume' : 'video_play',
                        videoDuration,
                        videoUrl,
                        videoId,
                        videoIndex: this.auctionCount,
                    });
                    this.playerStates.playing = true;
                    this.playerStates.paused = false;
                }
            });

            this.player.on('nextClick', (ev) => {
                this.sendLiftIgniterEvent({
                    ...ev,
                    e: 'video_next_click',
                    videoDuration,
                    videoUrl,
                    videoId,
                    videoIndex: this.auctionCount,
                });
                this.playerStates.playing = false;
                this.playerStates.paused = false;
            });

            this.player.on('playlist', (ev) => {
                this.sendLiftIgniterEvent({
                    ...ev,
                    e: 'video_playlist',
                    videoDuration,
                    videoUrl,
                    videoId,
                    videoIndex: this.auctionCount,
                });

                this.playerStates.playing = false;
                this.playerStates.paused = false;
            });

            this.player.on('pause', (ev) => {
                this.playerStates.paused = true;
                if (!this.playerStates.viewable) {
                    // see comment about ad_unfloat
                    this.sendLiftIgniterEvent({
                        ...ev,
                        e: 'video_unfloat',
                        videoDuration,
                        videoUrl,
                        videoId,
                        videoIndex: this.auctionCount,
                    });
                }
                this.sendLiftIgniterEvent({
                    ...ev,
                    e: 'video_pause',
                    videoDuration,
                    videoUrl,
                    videoId,
                    videoIndex: this.auctionCount,
                });
            });

            this.player.on('mute', (ev) => {
                if (this.adId) {
                    this.sendLiftIgniterEvent({
                        ...ev,
                        id: this.adId,
                        e: ev.mute ? 'video_ad_mute' : 'video_ad_unmute',
                    });
                } else {
                    this.sendLiftIgniterEvent({
                        ...ev,
                        videoDuration,
                        videoUrl,
                        videoId,
                        videoIndex: this.auctionCount,
                        e: ev.mute ? 'video_mute' : 'video_unmute',
                    });
                }
            });

            this.player.on('complete', (ev) => {
                this.playerStates.completed = true;
                this.sendLiftIgniterEvent({
                    ...ev,
                    e: 'video_complete',
                    videoDuration,
                    videoUrl,
                    videoId,
                    videoIndex: this.auctionCount,
                });
                this.playerStates.playing = false;
            });

            this.player.on('fullscreen', (ev) => {
                if (this.adId) {
                    this.sendLiftIgniterEvent({
                        ...ev,
                        e: ev.fullscreen ? 'video_ad_enter_full_screen' : 'video_ad_exit_full_screen',
                        id: this.adId,
                    });
                } else {
                    this.sendLiftIgniterEvent({
                        ...ev,
                        e: ev.fullscreen ? 'video_enter_full_screen' : 'video_exit_full_screen',
                        videoDuration,
                        videoUrl,
                        videoId,
                        videoIndex: this.auctionCount,
                    });
                }
            });

            if (device.get() === 'mobile' || device.get() === 'tablet') {
                // this is to detect iphone low power blocking autostart
                this.player.on('autostartNotAllowed', () => {
                    this.sendLiftIgniterEvent({
                        e: 'video_autostart_not_allowed',
                        videoDuration,
                        videoUrl,
                        videoId,
                        videoIndex: this.auctionCount,
                    });
                });
            }

            this.player.on('adRequest', (ev) => {
                this.sendLiftIgniterEvent({
                    ...ev,
                    e: 'video_ad_request_start',
                    videoDuration,
                    videoUrl,
                    videoId,
                    videoIndex: this.auctionCount,
                });
            });
        }

        _jwplayerStates() {
            this.isFloating = this.player.getFloating();
            this.isUserInactive = this.playerElement.classList.contains('jw-flag-user-inactive');
        }

        sendLiftIgniterEvent(jsonDetails) {
            if (typeof window !== 'undefined' && window.$p && jsonDetails) {
                if (jsonDetails['mediaFile']) {
                    jsonDetails['mediaFile'] = jsonDetails['mediaFile'].file;
                }

                if (jsonDetails['universalAdId'] && jsonDetails['universalAdId'].length > 0) {
                    jsonDetails['universalAdIdRegistry'] =
                        jsonDetails['universalAdId'][0].universalAdIdRegistry;
                    jsonDetails['universalAdIdValue'] = jsonDetails['universalAdId'][0].universalAdIdValue;
                }
                delete jsonDetails['universalAdId'];

                if (jsonDetails['adtitle']) {
                    jsonDetails['adTitle'] = jsonDetails['adtitle'];
                    delete jsonDetails['adtitle'];
                }

                if (jsonDetails['placement']) {
                    jsonDetails['adPlacement'] = jsonDetails['placement'];
                    delete jsonDetails['placement'];
                }

                if (jsonDetails['adposition']) {
                    jsonDetails['adPosition'] = jsonDetails['adposition'];
                    delete jsonDetails['adposition'];
                }

                delete jsonDetails['type'];

                for (let key in jsonDetails) {
                    if (jsonDetails[key] !== null && typeof jsonDetails[key] === 'object') {
                        jsonDetails[key] = JSON.stringify(jsonDetails[key]);
                    }

                    if (typeof jsonDetails[key] === 'string') {
                        // avoid 413 Request Entity Too Large
                        jsonDetails[key] = jsonDetails[key].substring(0, 1000);
                    }
                }

                window.$p('send', jsonDetails['e'], jsonDetails);
            }
        }

        sendPermutiveEvent(eventName, ad) {
            window.permutive.ready(() => {
                window.permutive.track(eventName, permutive.createEventObject(eventName, ad));
            }, 'initialised');
        }

        runAuction() {
            if (!this.auctionPromise) {
                const config = this.adConfig;

                if (!config || !config.units || this.directOnly) {
                    // Attempts to run an auction on a video without a config,
                    // such as a demonetized video,  will fail, since
                    // `dynamicAuction()` will wait forever. Resolve the
                    // promise and return to start the video. In the case of
                    // direct-only videos, we'll get the benefits of an ad call
                    // to try to fill it.
                    return Promise.resolve();
                }
                const { autostart, mute } = this.playerConfig;
                const breakpoint = pageClass.util.getCurrentBreakpoint();
                const units = config.units[breakpoint];
                config.autostartVideo = autostart == 'viewable';
                config.muteVideo = !!mute;
                units.forEach((unit) => {
                    let el = document.getElementById(unit.code);
                    if (!el) {
                        // Create a dummy container element for each ad slot
                        const div = document.createElement('div');
                        div.id = unit.code;
                        this.appendChild(div);
                    }
                });

                this.auctionCount++;
                const auctionId = `${this.id}-${this.auctionCount}`;
                const _self = this;
                this.auctionPromise = new Promise((resolve, reject) => {
                    window.phxAdAuctionQueue.push(function () {
                        config.slots.forEach((slot) => {
                            // This updates the adindex of every video slot
                            // that the prebid modules will use for logging
                            // before the auction starts
                            this.updateZoneIndex(slot.id, {
                                ...slot.model,
                                index: slot.model.index,
                            });
                        });
                        this.dynamicAuction(config, auctionId)
                            .then(() => {
                                // When the result comes back, we examine it to
                                // decide if we've cleared the autoplay
                                // threshold; we also resolve (or reject) the
                                // exterior auctionPromise.
                                const auctionResult = this.getResultById(auctionId);
                                auctionResult.then((result) => {
                                    if (result.getMaxBidForAdUnit() >= _self.autoplayThreshold) {
                                        _self.autoplayThresholdMet = true;
                                    }
                                });
                                auctionResult.then(resolve, reject);
                            })
                            .catch(() => resolve());
                    });
                });
            }

            return this.auctionPromise;
        }

        // Add current time position in URL hash so linked video page will skip to it
        goToLinkWithOffset(ev) {
            ev.preventDefault();
            let href = ev.target.getAttribute('href');
            let position = this.player.getPosition();
            if (position > 0) {
                href += `#t=${Math.floor(position)}`;
            }
            if (window.googleAnalytics) {
                window.googleAnalytics.sendEvent('Chained Video View', 'click', href);
            }
            window.location.href = href;
        }

        // Triggered by a scroll listener, unblocking our float-and-park
        // behavior. We then unregister our listener to avoid causing
        // performance issues.
        scrollListener() {
            this.observeVisibilityChanges();
            window.removeEventListener('scroll', this.floatScrollHandler);
        }

        onChangeToVisible() {
            this.isFloating = false;

            if (this.features.isEnabled('jw-float-placement')) {
                this.canFloat = false;
            }
        }

        onChangeToNotVisible() {
            if (this.features.isEnabled('jw-float-placement')) {
                this.canFloat = true;
                this.isFloating = true;
            } else {
                this.isFloating = this.canFloat;
            }
        }

        /**
         * JWPlayer callback for each video played within a playlist.
         * This handler is responsible for running a new ad auction for the
         * video being played. It will then construct ad tags an an ad schedule
         * for the video and returns the item with the updated `adschedule`
         * property.
         * @param {Object} item A JWPlayer playlist item object
         */
        onPlaylistItem(item, index) {
            // The first time a video ends this function is called with
            // index -1 and it's causing the adindex to increment twice.
            if (index === -1) {
                return Promise.resolve({});
            }

            if (item.mediaid !== this.videoId) {
                this.fetchWithBasePath(`/.api/video-meta-by-platform-ids/${item.mediaid}`)
                    .then((response) => {
                        return response.json();
                    })
                    .then((data) => {
                        let videoMeta = data[item.mediaid] || {};
                        const setLink = (metaEl) => {
                            if (!metaEl) {
                                return;
                            }
                            let link = metaEl.querySelector('a');
                            let path = videoMeta.path || null;
                            if (window.location.pathname === path) {
                                path = null;
                            }
                            if (path) {
                                link.setAttribute('href', path);
                            } else {
                                link.removeAttribute('href');
                            }
                            link.textContent = videoMeta.promoTitle || item.title || '';
                            if (!this.listenersAdded) {
                                link.addEventListener('click', this.goToLinkWithOffset.bind(this));
                            }
                        };
                        setLink(this.staticFooterMeta);
                        setLink(this.floatFooterMeta);
                        this.listenersAdded = true;
                        this.analyticsModel.videoTitle = videoMeta.title || '';
                        this.analyticsModel.videoAuthorName = videoMeta.videoAuthorName || '';
                    })
                    .catch((err) => {
                        this._reportErrorMessage('Cannot load video metadata: ' + err);
                        throw err;
                    });
            }

            // Player is not configured for ads, so return item without further
            // modification.
            if (!this.playerConfig.advertising) {
                return Promise.resolve(item);
            }

            // We'll issue the auction for this video (for all ad breaks configured) and
            // populate the schedule. Documentation for this callback is available at
            // at
            // https://developer.jwplayer.com/jwplayer/docs/jw8-javascript-api-reference#section-jwplayer-set-playlist-item-callback-callback
            // https://www.jwplayer.com/developers/web-player-demos/external-header-bidding/
            // https://developer.jwplayer.com/jwplayer/docs/jw8-set-up-vpb-with-video-prebid-for-google-ad-manager

            // When this method is invoked, we will issue a new auction, using the
            // configuration available. Each ad break will be supplied as a unit, with the
            // bid requests for each of them. Once the auction completes, the adschedule
            // property of the playlist item will be updated with newly constructed ad tag
            // URLs to deliver on the bids received.
            return this.runAuction().then((result) => {
                // Note: result may be undefined if no auction was run.
                // Clear auction promise since we're consuming it now.
                this.auctionPromise = null;

                const config = this.adConfig;
                const breakpoint = pageClass.util.getCurrentBreakpoint();
                const units = config && config.units && config.units[breakpoint];
                if (!units) {
                    return Promise.resolve(item);
                }
                const pbjs = window.pbjs;
                const breaks = [...(config.midrollBreaks || [])];
                const slots = config.slots;

                item.adschedule = [];

                // We create a single promise for our nonlinear pseudo-slot, if
                // it's enabled, and then a set of promises for our instream
                // slots. When they all resolve, the item adschedule is
                // configured.
                let nonlinearPromise;
                if (config.nonlinear) {
                    nonlinearPromise = generateNonlinearTargeting({
                        gdprApplicable: this.gdprApplicable,
                    }).then((targeting) => {
                        if (targeting && targeting.tag) {
                            item.adschedule.push({
                                type: 'nonlinear',
                                offset: config.nonlinear.offset || 5,
                                tag: targeting.tag,
                            });
                        } else {
                            // eslint-disable-next-line no-console
                            console.warn('Failed to receive nonlinear tag');
                        }
                    });
                } else {
                    nonlinearPromise = Promise.resolve();
                }

                let adPromises = slots.map((slot, index) => {
                    // We can't do *any* ad work with no slot.
                    if (!slot) {
                        return Promise.resolve();
                    }

                    let unitIndex = Math.min(index, 4);
                    let adUnit = units && units[unitIndex];
                    let adCode;
                    // If we don't have a defined unit, we'll skip getting
                    // responses for our non-existent ad unit later, since we
                    // can't have run an auction; we do want to have a variable
                    // present regardless, for the adInfo targeting.
                    if (adUnit) {
                        adCode = adUnit.code;
                    }

                    let offset;
                    const zone = slot.model.zone;
                    if (['preroll', 'postroll'].includes(zone)) {
                        offset = zone.replace('roll', '');
                    } else {
                        offset = breaks.shift();
                    }
                    if (!offset) {
                        // FIXME: should we make up a default here?
                        return Promise.resolve();
                    }

                    const adInfo = {
                        adId: adCode,
                        videoPlatformId: item.mediaid,
                        slotModel: slot.model,
                        dimensions: this.dimensions,
                        maxDuration: config.maxDuration,
                        gdprApplicable: this.gdprApplicable,
                        directOnly: this.directOnly,
                        permutive: this.permutiveSegments,
                    };
                    return generateTargeting(adInfo)
                        .then((targeting) => {
                            // This is the actual adindex increment.
                            if (slot.model?.index != null) {
                                slot.model.index++;
                            }

                            let vastTagUrl = targeting.tag;
                            if (result) {
                                // If there is an auction result, allow it to apply targeting
                                // to the vast tag.
                                let opts = { adUnit };
                                return result.applyTargetingToVideoUrl(vastTagUrl, opts);
                            } else {
                                return vastTagUrl;
                            }
                        })
                        .then((url) => {
                            item.adschedule.push({ offset, tag: url });
                        });
                });
                adPromises.push(nonlinearPromise);

                return Promise.all(adPromises).then(() => item);
            });
        }

        /**
         * Builds the JWPlayer config object given the current state of the element.
         *
         * @readonly
         */
        get playerConfig() {
            let config = {
                playlist: this.playlist,
                aspectratio: '16:9',
                width: '100%',
            };

            // When the Spiny integration is active,
            // add the Pandora JWP Plugin to the player options when instantiating JWP Video Players.
            if (window.orion) {
                config.plugins = {
                    '//cdn-ext.spiny.ai/spec/jwp/pandora-jwp.js': {},
                };
            }

            if (this.hasAttribute('floating')) {
                config.floating = {
                    dismissible: true,
                    // Manually only
                    mode: 'never',
                };
            }
            if (this.dimensions) {
                config.height = this.dimensions.h + '';
                config.width = this.dimensions.w + '';
            }

            if (this.hasAttribute('autoplay') && !this.demonetized && this.autoplayThresholdMet) {
                config.autostart = 'viewable';

                if (this.hasAttribute('autoplaymuted')) {
                    config.mute = true;
                }
            }

            if (this.hasAttribute('autopause')) {
                config.autoPause = {
                    viewability: true,
                    pauseAds: !this.hasAttribute('floating'),
                };
            }

            if (this.hasAttribute('play-next') && this.recommendationsPlaylist) {
                const playlistId = this.getAttribute('recommendations-playlist');
                let related = {
                    displayMode: 'none',
                    onclick: 'play',
                };
                let playNext = this.getAttribute('play-next');
                if (playNext === 'click') {
                    related.oncomplete = 'show';
                } else if (playNext === 'instant') {
                    related.oncomplete = 'none';
                } else {
                    related.oncomplete = 'autoplay';
                    related.autoplaytimer = parseInt(playNext, 10);
                }
                config.related = related;
            }

            if (this.demonetized || this.getAttribute('ad') === 'disabled') {
                config.advertising = false;
                return config;
            }

            config.advertising = {
                client: 'googima',
                // The default of 4 for maxRedirects is insufficient when using bidding
                maxRedirects: 8,
                vpaidcontrols: true,
                vpaidmode: this.hasAttribute('vpaid') ? 'enabled' : 'disabled',
            };

            if (config.autostart && this.hasAttribute('autoplayadsmuted')) {
                config.advertising.autoplayadsmuted = true;
            }

            return config;
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this.unobserveVisibilityChanges();
            this._unmuteButton.removeEventListener('click', this.unmuteSound);
            this._muteButton.removeEventListener('click', this.muteSound);
        }
    }
    window.customElements.define('phoenix-video', PhoenixVideo);
    measure.end();
});
